/**
 * Hooks de dados de Bases de Referência (M3) sobre React Query — consomem a API
 * real (sem mocks estáticos). RF-M3-001/002/006.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useServices } from '../contexts/ServicesContext';
import {
  BaseVersaoItem,
  CargaBaseResponse,
  CONTENT_TYPE_BASE,
  EstadoCargaBase,
  Parametros,
  PatchParametros,
  TipoBaseUpload,
} from '../services/bases-types';

/** GET /bases — lista de versões por tipo. */
export function useBasesReferencia(): {
  bases: readonly BaseVersaoItem[];
  carregando: boolean;
  erro: boolean;
  recarregar: () => void;
} {
  const { bases } = useServices();
  const query = useQuery({
    queryKey: ['bases'],
    queryFn: () => bases.listar(),
  });
  return {
    bases: query.data ?? [],
    carregando: query.isLoading,
    erro: query.isError,
    recarregar: () => void query.refetch(),
  };
}

/** O S3 recusou o PUT do arquivo (URL expirada, Content-Type diferente…). */
export class EnvioBaseError extends Error {
  constructor(readonly status: number) {
    super(`PUT do arquivo recusado (${status})`);
  }
}

/**
 * Envio de nova versão (C-M3): pede a URL pré-assinada (POST /bases/{tipo}/upload-url),
 * faz o PUT do arquivo com o Content-Type do tipo e dispara a carga
 * (POST /bases/{tipo}) com a chave devolvida — o usuário nunca digita chave S3.
 */
export function useEnviarBase(): {
  enviar: (tipo: TipoBaseUpload, arquivo: File, notas?: string) => Promise<CargaBaseResponse>;
  enviando: boolean;
  erro: boolean;
} {
  const { bases } = useServices();
  const mutation = useMutation({
    mutationFn: async ({ tipo, arquivo, notas }: { tipo: TipoBaseUpload; arquivo: File; notas?: string }) => {
      const contentType = CONTENT_TYPE_BASE[tipo];
      const { uploadUrl, arquivoS3Key } = await bases.pedirUploadUrl(tipo, arquivo.name, contentType);
      const put = await fetch(uploadUrl, { method: 'PUT', body: arquivo, headers: { 'Content-Type': contentType } });
      if (!put.ok) {
        throw new EnvioBaseError(put.status);
      }
      return bases.criarCarga(tipo, arquivoS3Key, notas);
    },
  });
  return {
    enviar: (tipo, arquivo, notas) => mutation.mutateAsync({ tipo, arquivo, notas }),
    enviando: mutation.isPending,
    erro: mutation.isError,
  };
}

/**
 * GET /bases/cargas/{id} — acompanha a carga até `concluida`/`erro`; ao concluir,
 * recarrega a lista de versões.
 */
export function useEstadoCargaBase(cargaId: string | null, intervaloMs = 3000): EstadoCargaBase | undefined {
  const { bases } = useServices();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['bases', 'carga', cargaId],
    enabled: cargaId !== null,
    queryFn: async () => {
      const estado = await bases.estadoCarga(cargaId as string);
      if (estado.status === 'concluida') {
        void qc.invalidateQueries({ queryKey: ['bases'], exact: true });
      }
      return estado;
    },
    refetchInterval: (q) => (q.state.data?.status === 'processando' || !q.state.data ? intervaloMs : false),
  });
  return query.data;
}

/** GET /parametros — limiares de validação. */
export function useParametros(): {
  parametros: Parametros | undefined;
  carregando: boolean;
  erro: boolean;
} {
  const { bases } = useServices();
  const query = useQuery({
    queryKey: ['parametros'],
    queryFn: () => bases.lerParametros(),
  });
  return { parametros: query.data, carregando: query.isLoading, erro: query.isError };
}

/** PATCH /parametros — atualização parcial dos limiares. */
export function useAtualizarParametros(): {
  atualizar: (patch: PatchParametros) => Promise<Parametros>;
  salvando: boolean;
  erro: boolean;
} {
  const { bases } = useServices();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (patch: PatchParametros) => bases.atualizarParametros(patch),
    onSuccess: (data) => {
      qc.setQueryData(['parametros'], data);
    },
  });
  return {
    atualizar: (patch) => mutation.mutateAsync(patch),
    salvando: mutation.isPending,
    erro: mutation.isError,
  };
}
